import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import {
  ariaAttr,
  callAllHandlers,
  contains,
  focus,
  getRelatedTarget,
  isEmpty,
  mergeRefs,
  useControllableState,
  useFocusOnPointerDown,
  useUpdateEffect
} from '@mote/helpers'
import { KeyboardKey } from '@mote/helpers/constants'

/**
 * React hook for managing the inline renaming of some text.
 *
 * @see Docs https://chakra-ui.com/editable
 */
export function useEditableFunctions(props = {}) {
  const {
    onChange: onChangeProp,
    onCancel: onCancelProp,
    onSubmit: onSubmitProp,
    value: valueProp,
    isDisabled,
    defaultValue,
    startWithEditView,
    isPreviewFocusable = true,
    submitOnBlur = true,
    selectAllOnFocus = true,
    placeholder,
    onEdit: onEditProp,
    ...htmlProps
  } = props

  const defaultIsEditing = Boolean(startWithEditView && !isDisabled)

  const [isEditing, setIsEditing] = useState(defaultIsEditing)

  const [value, setValue] = useControllableState({
    defaultValue: defaultValue || '',
    value: valueProp,
    onChange: onChangeProp
  })

  /**
   * Keep track of the previous value, so if users
   * presses `cancel`, we can revert to it.
   */
  const [prevValue, setPrevValue] = useState(value)

  /**
   * Ref to help focus the input in edit mode
   */
  const inputRef = useRef(null)
  const previewRef = useRef(null)
  const editButtonRef = useRef(null)
  const cancelButtonRef = useRef(null)
  const submitButtonRef = useRef(null)

  useFocusOnPointerDown({
    ref: inputRef,
    enabled: isEditing,
    elements: [cancelButtonRef, submitButtonRef]
  })

  const isInteractive = !isEditing && !isDisabled

  useLayoutEffect(() => {
    if (isEditing) {
      focus(inputRef.current, {
        selectTextIfInput: selectAllOnFocus
      })
    }
  }, [])

  useUpdateEffect(() => {
    if (!isEditing) {
      focus(editButtonRef.current)
      return
    }

    focus(inputRef.current, {
      selectTextIfInput: selectAllOnFocus
    })

    onEditProp?.()
  }, [isEditing, onEditProp, selectAllOnFocus])

  const onEdit = useCallback(() => {
    if (isInteractive) {
      setIsEditing(true)
    }
  }, [isInteractive])

  const onUpdatePrevValue = useCallback(() => {
    setPrevValue(value)
  }, [value])

  const onCancel = useCallback(() => {
    setIsEditing(false)
    setValue(prevValue)
    onCancelProp?.(prevValue)
  }, [onCancelProp, setValue, prevValue])

  const onSubmit = useCallback(() => {
    setIsEditing(false)
    setPrevValue(value)
    onSubmitProp?.(value)
  }, [value, onSubmitProp])

  const onChange = useCallback(
    event => {
      setValue(event.currentTarget.value)
    },
    [setValue]
  )

  const onKeyDown = useCallback(
    event => {
      const { keyCode } = event

      const keyMap = {
        [KeyboardKey.Escape]: onCancel,
        [KeyboardKey.Enter]: event => {
          if (!event.shiftKey && !event.metaKey) {
            onSubmit()
          }
        }
      }

      const action = keyMap[keyCode]

      if (action) {
        event.preventDefault()
        action(event)
      }
    },
    [onCancel, onSubmit]
  )

  const onKeyDownWithoutSubmit = useCallback(
    event => {
      const { keyCode } = event

      const keyMap = {
        [KeyboardKey.Escape]: onCancel
      }

      const action = keyMap[keyCode]

      if (action) {
        event.preventDefault()
        action(event)
      }
    },
    [onCancel]
  )

  const isValueEmpty = isEmpty(value)

  const onBlur = useCallback(
    event => {
      const relatedTarget = getRelatedTarget(event)
      const targetIsCancel = contains(cancelButtonRef.current, relatedTarget)
      const targetIsSubmit = contains(submitButtonRef.current, relatedTarget)
      const isValidBlur = !targetIsCancel && !targetIsSubmit

      if (isValidBlur) {
        if (submitOnBlur) {
          onSubmit()
        } else {
          onCancel()
        }
      }
    },
    [submitOnBlur, onSubmit, onCancel]
  )

  const getPreviewProps = useCallback(
    (props = {}, ref = null) => {
      const tabIndex = isInteractive && isPreviewFocusable ? 0 : null
      return {
        ...props,
        ref: mergeRefs(ref, previewRef),
        children: isValueEmpty ? placeholder : value,
        showPlaceholder: isValueEmpty,
        hidden: isEditing,
        'aria-disabled': ariaAttr(isDisabled),
        tabIndex,
        onFocus: callAllHandlers(props.onFocus, onEdit, onUpdatePrevValue)
      }
    },
    [
      isDisabled,
      isEditing,
      isInteractive,
      isPreviewFocusable,
      isValueEmpty,
      onEdit,
      onUpdatePrevValue,
      placeholder,
      value
    ]
  )

  const getInputProps = useCallback(
    (props = {}, ref = null) => ({
      ...props,
      hidden: !isEditing,
      placeholder,
      ref: mergeRefs(ref, inputRef),
      disabled: isDisabled,
      'aria-disabled': ariaAttr(isDisabled),
      value,
      onBlur: callAllHandlers(props.onBlur, onBlur),
      onChange: callAllHandlers(props.onChange, onChange),
      onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown),
      onFocus: callAllHandlers(props.onFocus, onUpdatePrevValue)
    }),
    [
      isDisabled,
      isEditing,
      onBlur,
      onChange,
      onKeyDown,
      onUpdatePrevValue,
      placeholder,
      value
    ]
  )

  const getTextAreaProps = useCallback(
    (props = {}, ref = null) => ({
      ...props,
      hidden: !isEditing,
      placeholder,
      ref: mergeRefs(ref, inputRef),
      disabled: isDisabled,
      'aria-disabled': ariaAttr(isDisabled),
      value,
      onBlur: callAllHandlers(props.onBlur, onBlur),
      onChange: callAllHandlers(props.onChange, onChange),
      onKeyDown: callAllHandlers(props.onKeyDown, onKeyDownWithoutSubmit),
      onFocus: callAllHandlers(props.onFocus, onUpdatePrevValue)
    }),
    [
      isDisabled,
      isEditing,
      onBlur,
      onChange,
      onKeyDownWithoutSubmit,
      onUpdatePrevValue,
      placeholder,
      value
    ]
  )

  const getEditButtonProps = useCallback(
    (props = {}, ref = null) => ({
      'aria-label': 'Edit',
      ...props,
      type: 'button',
      onClick: callAllHandlers(props.onClick, onEdit),
      ref: mergeRefs(ref, editButtonRef),
      disabled: isDisabled
    }),
    [onEdit, isDisabled]
  )

  const getSubmitButtonProps = useCallback(
    (props = {}, ref = null) => ({
      ...props,
      'aria-label': 'Submit',
      ref: mergeRefs(submitButtonRef, ref),
      type: 'button',
      onClick: callAllHandlers(props.onClick, onSubmit),
      disabled: isDisabled
    }),
    [onSubmit, isDisabled]
  )

  const getCancelButtonProps = useCallback(
    (props = {}, ref = null) => ({
      'aria-label': 'Cancel',
      id: 'cancel',
      ...props,
      ref: mergeRefs(cancelButtonRef, ref),
      type: 'button',
      onClick: callAllHandlers(props.onClick, onCancel),
      disabled: isDisabled
    }),
    [onCancel, isDisabled]
  )

  return {
    isEditing,
    isDisabled,
    isValueEmpty,
    value,
    onEdit,
    onCancel,
    onSubmit,
    getPreviewProps,
    getInputProps,
    getTextAreaProps,
    getEditButtonProps,
    getSubmitButtonProps,
    getCancelButtonProps,
    htmlProps
  }
}
